/**
 * lib/api/client.ts
 * Shared HTTP core for the Suite backend client — split out of lib/api.ts
 * (mechanical refactor, zero behavior change).
 *
 * - Base URL comes from VITE_API_URL (build-time), defaulting to localhost:3000.
 * - Every request carries the Bearer token from the session (except login).
 * - Non-2xx responses are parsed as the kernel error envelope and rethrown as
 *   ApiError so callers can show `code` / `message` instead of a raw status.
 *
 * Every lib/api/<module>/<section>.ts file imports `request`/`ApiError`/
 * `API_BASE` from here (never from the barrel) — same posture as the
 * pre-existing split files (lib/api/ptw.ts, cuentas-pagar.ts,
 * attendance-events.ts), which now import from here too instead of
 * `../api.js` to avoid a barrel import cycle.
 */
import type { ErrorEnvelope } from '@suite/contracts-kernel';

/** Exported so split-out api/*.ts modules (e.g. cuentas-pagar.ts) can do a raw blob download without re-deriving the base URL. */
export const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000').replace(/\/+$/, '');

/** Thrown on any non-2xx response. Carries the parsed error envelope when present. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Per-field validation errors (populated when status === 422 and the backend sends errors[]). */
  readonly fieldErrors: { field: string; message: string }[];
  constructor(
    status: number,
    code: string,
    message: string,
    fieldErrors: { field: string; message: string }[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Reserved for the login call. Auth now rides on the httpOnly cookie, so this is a no-op. */
  anonymous?: boolean;
}

/**
 * Exported so feature-specific client modules (e.g. `lib/api/ptw.ts`) can
 * reuse the same fetch/error-envelope handling instead of duplicating it —
 * root CLAUDE.md: never let two implementations of the same concept coexist.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // Auth is the httpOnly `suite_session` cookie. credentials:'include' makes the
  // browser send it (and store any Set-Cookie) on cross-origin app↔api calls.
  // No Authorization header — the SPA never holds the token.
  const init: RequestInit = { method: opts.method ?? 'GET', headers, credentials: 'include' };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch {
    // Network failure / CORS rejection / server unreachable.
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }

  if (!res.ok) {
    let code = 'UNKNOWN';
    let message = `Request failed with status ${res.status}`;
    let fieldErrors: { field: string; message: string }[] = [];
    try {
      const envelope = (await res.json()) as Partial<ErrorEnvelope>;
      if (typeof envelope.code === 'string') code = envelope.code;
      if (typeof envelope.message === 'string') message = envelope.message;
      if (Array.isArray(envelope.errors)) {
        fieldErrors = envelope.errors.map((e) => ({ field: e.field, message: e.message }));
      }
    } catch {
      // Body was not JSON — keep the generic message.
    }
    throw new ApiError(res.status, code, message, fieldErrors);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
